/**
 * 기준정보 — 기능개선관리대장
 * 수기 등록. 화면 행번호는 HiGrid No. 컬럼을 쓴다.
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import { showAlert } from 'utils/toastBridge';
import {
  FEATURE_MODULE_OPTIONS,
  createFeatureUid,
  emptyFeature,
  loadFeatureList,
  saveFeatureList,
  validateFeatureRows,
} from 'master/featureImprovementStore';

const GRID_ID = 'master-feature-improvement';

const DATE_KIND_OPTIONS = [
  { value: 'devDate', label: '구현일자' },
  { value: 'appliedDate', label: '반영일자' },
  { value: 'deployDate', label: '배포일자' },
];

const FeatureImprovementLedgerInner = () => {
  const gridRef = useRef(null);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);

  const searchDefaults = useMemo(
    () => ({
      moduleCd: '',
      dateKind: 'devDate',
      dateFrom: '',
      dateTo: '',
    }),
    []
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues } = methods;

  const columns = useMemo(
    () => [
      hiCol.edComboColumn({
        header: '모듈',
        field: 'moduleCd',
        size: 130,
        required: true,
        options: FEATURE_MODULE_OPTIONS,
      }),
      hiCol.edTextColumn({ header: '항목', field: 'itemNm', size: 220, required: true }),
      hiCol.edTextColumn({ header: '내용', field: 'content', size: 280 }),
      hiCol.edDateColumn({ header: '구현일자', field: 'devDate', size: 118 }),
      hiCol.edDateColumn({ header: '반영일자', field: 'appliedDate', size: 118 }),
      hiCol.edDateColumn({ header: '배포일자', field: 'deployDate', size: 118 }),
      hiCol.roTextColumn({ header: '진행', field: 'progressStatus', size: 88, align: 'center' }),
      hiCol.edTextColumn({ header: '비고1', field: 'remark1', size: 160 }),
      hiCol.edTextColumn({ header: '비고2', field: 'remark2', size: 160 }),
    ],
    []
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: emptyFeature({ moduleCd: 'BUD' }),
    getDefaultRow: () => emptyFeature({ featureUid: createFeatureUid(), moduleCd: 'BUD', seqNo: '' }),
    duplicateExcludeKeys: ['featureUid', 'seqNo', 'progressStatus'],
    customOnSearch: async () => {
      const f = getValues();
      return loadFeatureList({
        moduleCd: f.moduleCd,
        dateKind: f.dateKind,
        dateFrom: f.dateFrom,
        dateTo: f.dateTo,
      });
    },
    customOnSave: async ({ saveData, setData }) => {
      const dirty = (saveData || []).map((r) => ({
        ...r,
        featureUid: r.featureUid || createFeatureUid(),
      }));
      const errors = validateFeatureRows(dirty);
      if (errors.length) {
        showAlert(errors.slice(0, 8).join('\n'));
        throw new Error(errors[0]);
      }
      await saveFeatureList(dirty);
      const f = getValues();
      const refreshed = await loadFeatureList({
        moduleCd: f.moduleCd,
        dateKind: f.dateKind,
        dateFrom: f.dateFrom,
        dateTo: f.dateTo,
      });
      setData(refreshed);
      showAlert('저장되었습니다.');
    },
  });

  const handleSearch = useCallback(() => {
    grid.handleSearch().catch((e) => showAlert(e.message || '조회 실패'));
  }, [grid]);

  const handleSave = useCallback(() => {
    grid.handleSave().catch((e) => {
      if (e?.message) return;
      showAlert('저장 실패');
    });
  }, [grid]);

  useEffect(() => {
    handleSearch();
    // 최초 진입만
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getData = useCallback(
    () =>
      (gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? []).filter(
        (r) => r.mod !== 'D'
      ),
    []
  );
  const getDataColumns = useCallback(
    () => gridRef.current?.getDataColumns?.() ?? columns,
    [columns]
  );

  useRegisterGridExport({
    id: GRID_ID,
    label: '기능개선관리대장',
    getData,
    getDataColumns,
    order: 0,
  });
  const { excelButton, dialog: excelDialog } = useExcelExportAction({
    fileName: '기능개선관리대장',
  });

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'moduleCd',
          label: '모듈',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...FEATURE_MODULE_OPTIONS],
          width: '160px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'dateKind',
          label: '기간기준',
          type: 'select',
          options: DATE_KIND_OPTIONS,
          width: '140px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'period',
          label: '기간',
          type: 'dateRange',
          startFieldName: 'dateFrom',
          endFieldName: 'dateTo',
          width: '240px',
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    []
  );

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      { id: 'add', label: '추가', icon: '➕', onClick: () => grid.handleAdd(), hotkey: 'F8' },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: () => grid.handleDelete(),
        hotkey: 'F9',
        variant: 'danger',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
      },
      excelButton,
    ],
    [handleSearch, handleSave, grid, excelButton]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} loading={grid.loading} />
        <CollapsibleSearchPanel storageKey={GRID_ID} prefsUserId={prefsUserId} title="조회조건">
          <FormArea compact fields={searchFields} onFieldEnter={handleSearch} />
        </CollapsibleSearchPanel>
      </FormProvider>
      <GridExportFocusScope gridId={GRID_ID} style={{ flex: 1, minHeight: 0 }}>
        <HiGridComponent
          ref={gridRef}
          columns={columns}
          height="100%"
          showRowNumber
          rowNumberSize={56}
          showModColumn
          enableColumnFilters
          enableContextMenu
          enableRowEditMenu
          enableCut
          enableFindReplace
          allowReplace
          enableHeaderMenu
          enableSelectionStats
          enableFreeze
          enableQuickExport
          exportFileName="기능개선관리대장"
          gridId={GRID_ID}
          prefsUserId={prefsUserId}
          {...grid.contextMenuProps}
        />
      </GridExportFocusScope>
      {excelDialog}
    </PageContainer>
  );
};

export default function FeatureImprovementLedger() {
  return (
    <GridExportProvider>
      <FeatureImprovementLedgerInner />
    </GridExportProvider>
  );
}
